import "./runtime";

/**
 * Eventos de e-commerce da loja, no vocabulário do GA4. Cada chamada vira:
 *
 *  - `dataLayer.push({ event, ecommerce })` — o que um contêiner do GTM lê;
 *  - `gtag('event', …)` — GA4 e Google Ads ligados direto em Integrações;
 *  - `fbq('track', …)` — Meta Pixel, com o nome e os campos dela;
 *  - na compra, a conversão do Google Ads (`send_to: AW-…/rótulo`).
 *
 * Tudo isto respeita o consentimento: com o cookie negado o gtag manda só
 * sinal sem cookie e a Meta não envia nada (ver lib/consent). Loja sem
 * nenhuma tag não tem `window.b2bTracking` e cada chamada é um no-op.
 */

/** Item no formato do GA4. `item_id` é o productId — o mesmo do feed (Fase 4). */
export interface TrackItem {
    item_id: string;
    item_name: string;
    item_brand?: string;
    price: number;
    quantity: number;
    index?: number;
    item_list_id?: string;
    item_list_name?: string;
}

export const CURRENCY = "BRL";

interface ItemsPayload {
    items: TrackItem[];
    /** Padrão: soma de preço × quantidade dos itens. */
    value?: number;
}

export type TrackEvent =
    | { name: "view_item_list"; list: { id: string; name: string }; items: TrackItem[] }
    | { name: "view_item"; item: TrackItem }
    | { name: "search"; term: string }
    | ({ name: "add_to_cart" | "remove_from_cart" | "view_cart" | "begin_checkout" } & ItemsPayload)
    | ({ name: "add_shipping_info"; shippingTier: string } & ItemsPayload)
    | ({ name: "add_payment_info"; paymentType: string } & ItemsPayload)
    | ({
          name: "purchase";
          transactionId: string;
          value: number;
          shipping: number;
          coupon?: string;
          paymentType?: string;
      } & Omit<ItemsPayload, "value">);

/** Nome do evento padrão da Meta; ausente = a Meta não recebe este evento. */
const META_EVENT: Partial<Record<TrackEvent["name"], string>> = {
    view_item: "ViewContent",
    search: "Search",
    add_to_cart: "AddToCart",
    begin_checkout: "InitiateCheckout",
    add_payment_info: "AddPaymentInfo",
    purchase: "Purchase",
};

const round2 = (value: number) => Math.round(value * 100) / 100;

export function itemsValue(items: TrackItem[]): number {
    return round2(items.reduce((sum, item) => sum + item.price * item.quantity, 0));
}

/** Corpo `ecommerce` do GA4 para o evento. */
export function ga4Payload(event: TrackEvent): Record<string, unknown> {
    switch (event.name) {
        case "view_item_list":
            return {
                item_list_id: event.list.id,
                item_list_name: event.list.name,
                items: event.items.map((item, index) => ({
                    ...item,
                    index,
                    item_list_id: event.list.id,
                    item_list_name: event.list.name,
                })),
            };
        case "view_item":
            return { currency: CURRENCY, value: itemsValue([event.item]), items: [event.item] };
        case "search":
            return { search_term: event.term };
        case "purchase":
            return {
                transaction_id: event.transactionId,
                currency: CURRENCY,
                value: round2(event.value),
                shipping: round2(event.shipping),
                ...(event.coupon ? { coupon: event.coupon } : {}),
                ...(event.paymentType ? { payment_type: event.paymentType } : {}),
                items: event.items,
            };
        default: {
            const extra =
                event.name === "add_shipping_info"
                    ? { shipping_tier: event.shippingTier }
                    : event.name === "add_payment_info"
                      ? { payment_type: event.paymentType }
                      : {};
            return {
                currency: CURRENCY,
                value: event.value ?? itemsValue(event.items),
                ...extra,
                items: event.items,
            };
        }
    }
}

/** Campos da Meta: ids + `contents`, valor e moeda. */
export function metaPayload(event: TrackEvent): Record<string, unknown> {
    if (event.name === "search") return { search_string: event.term };

    const items =
        event.name === "view_item" ? [event.item] : event.name === "view_item_list" ? event.items : event.items;
    const value =
        event.name === "purchase"
            ? round2(event.value)
            : event.name === "view_item" || event.name === "view_item_list"
              ? itemsValue(items)
              : (event.value ?? itemsValue(items));

    return {
        content_type: "product",
        content_ids: items.map((item) => item.item_id),
        contents: items.map((item) => ({ id: item.item_id, quantity: item.quantity, item_price: item.price })),
        num_items: items.reduce((sum, item) => sum + item.quantity, 0),
        value,
        currency: CURRENCY,
    };
}

/**
 * ID do evento — a Meta usa para casar o evento do browser com o do servidor
 * (Conversions API, Fase 3). Na compra é determinístico: o servidor sabe
 * gerar o mesmo a partir do número do pedido.
 */
export function eventId(event: TrackEvent): string {
    if (event.name === "purchase") return `purchase-${event.transactionId}`;
    const random =
        typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `${event.name}-${random}`;
}

export function track(event: TrackEvent): void {
    if (typeof window === "undefined") return;
    const runtime = window.b2bTracking;
    if (!runtime) return;

    try {
        const ecommerce = ga4Payload(event);
        const id = eventId(event);

        // Zera o `ecommerce` anterior: o dataLayer do GTM faz merge raso, e
        // itens de um evento vazariam para o próximo.
        window.dataLayer?.push({ ecommerce: null });
        window.dataLayer?.push({ event: event.name, event_id: id, ecommerce });

        if (runtime.gtag) {
            window.gtag?.("event", event.name, ecommerce);
            if (event.name === "purchase" && runtime.adsConversion) {
                window.gtag?.("event", "conversion", {
                    send_to: runtime.adsConversion,
                    value: ecommerce.value,
                    currency: CURRENCY,
                    transaction_id: event.transactionId,
                });
            }
        }

        const metaName = META_EVENT[event.name];
        if (runtime.meta && metaName) {
            window.fbq?.("track", metaName, metaPayload(event), { eventID: id });
        }
    } catch {
        // Medição nunca pode quebrar a compra.
    }
}

/**
 * Marca que um evento único (a compra de um pedido) já foi enviado. Fica no
 * localStorage: voltar para a página ou abrir outra aba não conta de novo.
 */
const SENT_KEY = "b2b_tracked_purchases";

export function markSentOnce(key: string): boolean {
    try {
        const sent: string[] = JSON.parse(window.localStorage.getItem(SENT_KEY) ?? "[]");
        if (sent.includes(key)) return false;
        window.localStorage.setItem(SENT_KEY, JSON.stringify([...sent, key].slice(-50)));
        return true;
    } catch {
        return true;
    }
}
